import { AlertTriangle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import type { TableOverlap } from '@/types/connectors';

interface Props { overlaps?: TableOverlap[] | null; }

/** #2190 — tables this run wrote that another ENABLED configuration of the same
 *  type also wrote recently. The two runs overwrite each other's shared columns,
 *  so the last to finish wins. Shown on any terminal status: the run itself
 *  succeeded. The fix is to exclude the account in one of the two configs. */
export function TableOverlapPanel({ overlaps }: Props) {
  if (!overlaps?.length) return null;
  const configs = new Map<string, string>();
  for (const o of overlaps) configs.set(String(o.config_id), o.config_name);
  return (
    <Alert variant="warning" data-testid="table-overlap-panel">
      <AlertTriangle className="h-4 w-4" aria-hidden />
      <AlertTitle>Another configuration writes the same tables</AlertTitle>
      <AlertDescription>
        <p className="mb-1">
          {[...configs].map(([id, n]) => `config ${id} (${n})`).join(', ')} also wrote{' '}
          {overlaps.length === 1 ? 'this table' : 'these tables'} in the last 7 days. The
          last run to finish wins on shared columns. Exclude the overlapping accounts in one
          of the two configurations.
        </p>
        <ul className="space-y-0.5">
          {overlaps.map((o) => (
            <li key={`${o.config_id}:${o.table}`}>
              <span className="font-semibold">{o.table}</span> — config {o.config_id}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
